/** @jsxImportSource @emotion/react */
import Box from '@mui/material/Box';
import { useTheme } from '@emotion/react';
import CheckBox from '@CheckBox';
import IconTileCheckBox from '@IconTileCheckBox';
import ListLayout from '@ListLayout';
import TileLayout from '@TileLayout';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import QuestionText from '../QuestionText/QuestionText';
import {
  isIconForEveryOption,
  swapS3DomainWithPublicAssetsDomain,
  getSupplierMatchComponent,
} from '../__helpers__/component.helpers';
import { renderMustache } from '../../Helpers/mustache';
import useGetExperimentName from '../../Hooks/useGetExperimentName/useGetExperimentName';
import { SUBCATEGORY_MAX_MATCHES_DEFAULT } from '../../Helpers/supplierMatches';
import { filterResellers } from '../../Helpers/dynamicSelectAndConnect';
import { getSelectAndConnectComponent } from '../../Helpers/supplierMatchPredicates';
import {
  dedupeOptionsById,
  getSelectAndConnectMatchOptions,
} from '../../Helpers/supplierResolution';

const customerMatchToOption = (match) => ({
  id: match.brandId,
  value: match.brandLabel,
  icon_url: match.brandLogoUrl,
});

const SelectedSupplierMatches = () => {
  const [s3EnvDomain, setS3EnvDomain] = useState(
    process.env.NEXT_PUBLIC_CHAMELEON_CLIENT_ASSETS_URL
  );
  const theme = useTheme();

  // Add fallback empty objects to prevent errors if store properties are undefined
  const components = useSelector((store) => store?.components || {});
  const { iFrameId } = useSelector((store) => store?.optionalFormConfig || {});
  const {
    formId,
    showCustomerMatchesOnThankYouPage,
    supplierMatchHeadingOnThankYouPage,
  } = useSelector((store) => store?.formConfig || {});
  const {
    customerMatches,
    subcategoryMatches,
    subcategoryMaxMatches,
    dynamicMatches,
  } = useSelector((store) => store?.supplierMatches || {});
  const dynamicSelectAndConnect = useSelector(
    (store) => store?.features?.dynamicSelectAndConnect
  );

  // Get supplier match component safely
  const supplierMatchComponent = getSupplierMatchComponent({ components });
  const componentId = supplierMatchComponent?.component?.id || '';

  const selectAndConnectComponent = getSelectAndConnectComponent({
    components,
  });

  const customerMatchOptions = customerMatches?.map(customerMatchToOption);

  const subcategoryMatchOptions = subcategoryMatches?.map(
    customerMatchToOption
  );

  const selectAndConnectMatchOptions = getSelectAndConnectMatchOptions(
    selectAndConnectComponent
  );

  // Combine the predefined supplier (from customerMatches) with the user's
  // Select and Connect selections so the thank-you page reflects everything
  // the customer kept selected, not just the gateway-returned predefined brand.
  const customerAndSelectAndConnectOptions = dedupeOptionsById(
    customerMatchOptions || [],
    selectAndConnectMatchOptions
  );

  const input = supplierMatchComponent?.input || {};

  /**
   * Note: This is the order of precedence for options:
   * 1. Supplier match component (excluding what has been deselected)
   * 2. Customer matches combined with Select and Connect selections
   * 3. Subcategory matches
   *
   * The customer is being matched to a "brand", but as a company we refer to brands
   * as suppliers. There may be one or many suppliers under one brand.
   */
  let options = [];

  if (dynamicSelectAndConnect && dynamicMatches?.length > 0) {
    const nonResellerMatches = filterResellers(dynamicMatches);
    options = nonResellerMatches.map((match) => ({
      id: match.brandId || match.supplierId,
      value: match.brandName || match.brandLabel,
      icon_url: match.brandLogoUrl,
    }));
  } else if (input?.options && input.options.length > 0) {
    options = input.options;
  } else if (customerAndSelectAndConnectOptions.length > 0) {
    options = customerAndSelectAndConnectOptions;
  } else if (subcategoryMatchOptions && subcategoryMatchOptions.length > 0) {
    options = subcategoryMatchOptions;
  }

  // Determine layout type safely
  const allOptionsHaveIcons = isIconForEveryOption(options);
  const componentProps = supplierMatchComponent?.component || {};
  const forceListLayout = componentProps?.forceListLayout || false;

  const isTiled =
    (supplierMatchComponent || showCustomerMatchesOnThankYouPage) &&
    allOptionsHaveIcons &&
    !forceListLayout;

  // Call all experiment hooks unconditionally
  const checkboxExperimentName = useGetExperimentName({
    componentId,
    componentName: 'CheckBox',
  });
  const iconCheckboxExperimentName = useGetExperimentName({
    componentId,
    componentName: 'IconTileCheckBox',
  });
  const layoutExperimentName = useGetExperimentName({
    componentId,
    componentName: isTiled ? 'TileLayout' : 'ListLayout',
  });

  // Update s3EnvDomain if needed
  useEffect(() => {
    if (s3EnvDomain !== process.env.NEXT_PUBLIC_CHAMELEON_CLIENT_ASSETS_URL) {
      setS3EnvDomain(process.env.NEXT_PUBLIC_CHAMELEON_CLIENT_ASSETS_URL);
    }
  }, [s3EnvDomain]);

  // Early return if no component ID or components - AFTER all hooks are called
  if (!showCustomerMatchesOnThankYouPage) {
    return null;
  }

  // Continue with component rendering logic
  const areOptionLabelsHidden = input?.areOptionLabelsHidden || false;
  const text = supplierMatchHeadingOnThankYouPage || '';
  const Layout = isTiled ? TileLayout : ListLayout;
  const shouldShowLabel = !areOptionLabelsHidden || !allOptionsHaveIcons;

  // When a supplier-match component drove the form, the user may have
  // deselected some of its gateway-returned matches; honour those choices.
  // Other paths already arrive pre-filtered to what we want to display.
  const supplierMatchSelectedIds = input?.selectedOptions || [];
  let filteredOptions =
    supplierMatchComponent && options.length > 0
      ? options.filter((option) =>
          supplierMatchSelectedIds.includes(String(option?.id))
        )
      : options;

  if (!filteredOptions?.length > 0) {
    return null;
  }

  // We would have already filtered to the options displayed in the supplier match component
  // if it exists, so we only need to filter the options by subcategory max matches now
  // This is only used when there is no supplier match component on the page
  const maxMatchesToDisplay =
    subcategoryMaxMatches || SUBCATEGORY_MAX_MATCHES_DEFAULT;
  if (filteredOptions.length > maxMatchesToDisplay) {
    filteredOptions = filteredOptions.slice(0, maxMatchesToDisplay);
  }

  return (
    <>
      <QuestionText
        heading={renderMustache({
          template: text,
          components,
          currentComponent: supplierMatchComponent,
          iFrameId,
          formId,
        })}
      />
      <Box
        marginTop={theme.mvf.spacing.medium}
        data-testid="supplierMatchContainer"
      >
        <Layout experimentName={layoutExperimentName}>
          {filteredOptions.map((option) => {
            const { value, id } = option;
            const optionIdString = String(id);
            const iconUrl = option.icon_url;

            // Only attempt to process the URL if it exists
            const strippedUrl = iconUrl
              ? swapS3DomainWithPublicAssetsDomain(
                  iconUrl,
                  process.env.NEXT_PUBLIC_ASSETS_URL
                )
              : undefined;

            const Icon =
              allOptionsHaveIcons && strippedUrl ? (
                <img
                  style={{ maxWidth: '100%', height: isTiled ? 60 : 40 }}
                  src={strippedUrl}
                  alt="optionImage"
                />
              ) : undefined;

            return isTiled ? (
              <IconTileCheckBox
                key={optionIdString || `option-${Math.random()}`}
                id={optionIdString}
                onClick={() => {}}
                Icon={Icon}
                text={value}
                showText={shouldShowLabel}
                experimentName={iconCheckboxExperimentName}
                selected
                disabled
                hideCheckbox
              />
            ) : (
              <CheckBox
                Icon={Icon}
                id={optionIdString}
                key={optionIdString || `option-${Math.random()}`}
                onClick={() => {}}
                textAlign="center"
                text={value}
                showText={shouldShowLabel}
                experimentName={checkboxExperimentName}
                selected
                disabled
                hideCheckbox
              />
            );
          })}
        </Layout>
      </Box>
    </>
  );
};

export default SelectedSupplierMatches;
